export interface IAttachmentTagUnit {
  id: number;
  name: string;
  isParcelUnit?: boolean;
}

export interface IAttachmentTagContext {
  units: IAttachmentTagUnit[];
  inspectionUnitId?: number;
}

export interface IAttachmentTagFile {
  fileName?: string | null;
  fileURL?: string | null;
  mimeType?: string | null;
  locationUnitId?: number | null;
  locationUnit?: {
    id?: number;
    name?: string;
    isParcelUnit?: boolean;
  } | null;
  tagChanged?: boolean;
}

const TAGGABLE_IMAGE_EXTENSIONS = new Set([
  'bmp',
  'gif',
  'heic',
  'heif',
  'jpe',
  'jpeg',
  'jpg',
  'png',
  'tif',
  'tiff',
  'webp',
]);

const fileExtension = (value?: string | null): string => {
  if (!value) {
    return '';
  }

  const path = value.split('?')[0].split('#')[0];
  const extension = path.split('.').pop();

  return extension && extension !== path ? extension.toLowerCase() : '';
};

export const isTaggableImageMime = (mimeType?: string | null): boolean =>
  typeof mimeType === 'string' && mimeType.trim().toLowerCase().startsWith('image/');

export const isTaggableImageName = (fileName?: string | null): boolean =>
  TAGGABLE_IMAGE_EXTENSIONS.has(fileExtension(fileName));

// A stored or browser MIME decides the image. The extension is only a fallback when no MIME exists,
// so a PDF stored under a .jpg name stays untaggable and an empty HEIC type can still be tagged.
export const isTaggableImageFile = (file: IAttachmentTagFile): boolean => {
  const mimeType = (file.mimeType || '').trim();

  if (mimeType) {
    return isTaggableImageMime(mimeType);
  }

  return isTaggableImageName(file.fileName) || isTaggableImageName(file.fileURL);
};

export const hasRegularUnits = (units?: Array<{ isParcelUnit?: boolean }> | null): boolean =>
  (units || []).some((unit) => !unit.isParcelUnit);

export const attachmentTagLabel = (unit?: { name?: string; isParcelUnit?: boolean } | null): string => {
  if (!unit) {
    return 'Untagged';
  }

  return unit.isParcelUnit ? 'Parcel' : `Unit ${unit.name}`;
};

export const attachmentTagSelectLabel = (unit: { name: string; isParcelUnit?: boolean }): string =>
  unit.isParcelUnit ? 'Parcel' : `Unit ${unit.name}`;

export const attachmentTagLabelForFile = (file: IAttachmentTagFile, units: IAttachmentTagUnit[] = []): string => {
  if (Object.prototype.hasOwnProperty.call(file, 'locationUnitId') && file.locationUnitId !== undefined) {
    if (file.locationUnitId == null) {
      return 'Untagged';
    }

    const selected = units.find((unit) => unit.id === file.locationUnitId);

    return selected ? attachmentTagSelectLabel(selected) : attachmentTagLabel(file.locationUnit);
  }

  if (file.locationUnit) {
    return attachmentTagLabel(file.locationUnit);
  }

  return 'Untagged';
};

export const toAttachmentTagContext = (
  units?: Array<{ id: number; name: string; isParcelUnit?: boolean }> | null,
  inspectionUnitId?: number
): IAttachmentTagContext | undefined => {
  const tagUnits = (units || []).map((unit) => ({
    id: unit.id,
    name: unit.name,
    isParcelUnit: !!unit.isParcelUnit,
  }));

  if (!hasRegularUnits(tagUnits)) {
    return undefined;
  }

  if (typeof inspectionUnitId === 'number') {
    return { units: tagUnits, inspectionUnitId };
  }

  return { units: tagUnits };
};

export const defaultAttachmentTagId = (context?: IAttachmentTagContext): number | null =>
  typeof context?.inspectionUnitId === 'number' ? context.inspectionUnitId : null;

export const attachmentTagOptions = (context: IAttachmentTagContext): Array<{ value: number | ''; label: string }> => {
  const options: Array<{ value: number | ''; label: string }> = [];

  if (typeof context.inspectionUnitId !== 'number') {
    options.push({ value: '', label: 'Untagged' });
  }

  context.units.forEach((unit) => {
    options.push({ value: unit.id, label: attachmentTagSelectLabel(unit) });
  });

  return options;
};

export const storedAttachmentTagId = (file: IAttachmentTagFile): number | null => {
  if (file.locationUnit && file.locationUnit.id != null) {
    return file.locationUnit.id;
  }

  if (file.locationUnit === null) {
    return null;
  }

  return file.locationUnitId ?? null;
};

// A photo already tagged to this unit is added as-is. Another unit and the Parcel share the
// Continue/Cancel warning. Untagged photos get their own Tag-to / Leave-untagged choice.
export type ExistingPhotoSelection = 'current' | 'mismatch' | 'untagged';

export const classifyExistingPhotoSelection = (
  file: IAttachmentTagFile,
  currentUnitId: number
): ExistingPhotoSelection => {
  const tagId = storedAttachmentTagId(file);

  if (tagId == null) {
    return 'untagged';
  }

  return tagId === currentUnitId ? 'current' : 'mismatch';
};

export const nextAttachmentTagState = (
  attachment: IAttachmentTagFile & { originalLocationUnitId?: number | null },
  selectedLocationUnitId: number | null,
  units: IAttachmentTagUnit[]
): {
  selectedLocationUnitId: number | null;
  tagChanged: boolean;
  locationUnitId: number | null;
  locationUnit: { id: number; name: string; isParcelUnit?: boolean } | null;
} => {
  const unit =
    selectedLocationUnitId == null ? null : units.find((candidate) => candidate.id === selectedLocationUnitId) || null;
  const current = attachment.locationUnit;
  const locationUnit =
    selectedLocationUnitId == null
      ? null
      : unit
      ? { id: unit.id, name: unit.name, isParcelUnit: !!unit.isParcelUnit }
      : current && current.id != null && current.name != null
      ? { id: current.id, name: current.name, isParcelUnit: !!current.isParcelUnit }
      : null;

  return {
    selectedLocationUnitId,
    tagChanged: selectedLocationUnitId !== (attachment.originalLocationUnitId ?? null),
    locationUnitId: selectedLocationUnitId,
    locationUnit,
  };
};

// Display-only fields stay in local state. A missing locationUnitId is omitted; null is kept so an
// explicit clear survives JSON.stringify.
export const sanitizeAttachmentRequest = <T extends Record<string, any>>(attachment: T): T => {
  const next: any = { ...attachment };

  delete next.tagChanged;
  delete next.locationUnit;
  delete next.selectedLocationUnitId;
  delete next.originalLocationUnitId;

  if (next.locationUnitId === undefined) {
    delete next.locationUnitId;
  }

  return next;
};
